import React, { useState } from "react";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { SubmitButtonIcon } from "./submit-button-icon";
import "./stylesheet.css";

interface ExpandableTextareaProps extends React.ComponentProps<"textarea"> {
  label?: string;
  required?: boolean;
  expandedHeight?: string;
  initialHeight?: string;
  onSubmit?: () => void;
  isSubmitting?: boolean;
}

export const ExpandableTextarea = React.forwardRef<
  HTMLTextAreaElement,
  ExpandableTextareaProps
>(
  (
    {
      className,
      label,
      required,
      expandedHeight,
      initialHeight,
      placeholder,
      onSubmit,
      isSubmitting = false,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const hasText =
      props.value &&
      typeof props.value === "string" &&
      props.value.trim().length > 0;

    const isExpanded = isSubmitting || isFocused || hasText;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === "Enter" &&
        hasText &&
        onSubmit
      ) {
        e.preventDefault();
        onSubmit();
        setIsFocused(false);
      }
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };

    return (
      <div className="flex w-full flex-col gap-2 px-px">
        {label && (
          <Typography
            variant="subtitleW3"
            textColor="tertiary"
            className="mb-1"
          >
            {label}
            {required && <span className="ml-1 text-red-600">*</span>}
          </Typography>
        )}
        <div
          className={cn(
            "relative transition-all duration-300 ease-in-out",
            isExpanded ? expandedHeight : initialHeight,
          )}
        >
          <textarea
            ref={ref}
            className={cn(
              "absolute inset-0 w-full rounded-lg border border-stroke-primary bg-transparent px-4 py-4 text-lg shadow-sm transition-all duration-300 placeholder:text-tertiary-text focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              hasText ? "pr-12" : "",
              className,
            )}
            placeholder={placeholder || label || ""}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />

          {hasText && onSubmit && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (!isSubmitting) onSubmit();
              }}
              className={cn(
                "submit-button",
                isSubmitting && "submit-button-loading",
              )}
              aria-label="Submit note"
              disabled={isSubmitting}
            >
              {!isSubmitting && <SubmitButtonIcon />}
            </button>
          )}
        </div>
      </div>
    );
  },
);

ExpandableTextarea.displayName = "ExpandableTextarea";
